import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, inject, LOCALE_ID } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { FeatureFlag } from '@core/enums';
import { AccountService } from '@core/services';
import { Store } from '@ngxs/store';
import { ReadableFieldPipe } from '@shared/pipes/readable-field.pipe';
import { AccountState } from '@state/account/state';
import { FieldDef } from 'app/core/models';
import { DataTableModel } from 'app/modules/reporting/reporting.types';
import { RelatedToType } from 'app/shared/enums';
import { GraphqlFilterSort } from 'app/shared/models/graphql';

@Component({
  selector: 'app-time-series-data-table',
  templateUrl: './time-series.component.html',
  styleUrls: ['./time-series.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSeriesDataTableComponent {
  @Input() set data(value: DataTableModel) {
    this._data = value;
    if (value.timeSeries?.isGrouped) {
      this.columns = value.timeSeries.columns;
      this.displayColumns = (this.columns as unknown as FieldDef[])
        .map((x) => x.name)
        .filter((x) => !['id', 'pipeline_id'].includes(x));
    } else {
      this.columns = ['name', ...(value.timeSeries?.columns ?? [])];
      this.selectedFields = [...(value.timeSeries?.columns.map((column) => this.convertToField(column)) ?? [])];
    }
  }
  @Input('fields') set _fields(value: FieldDef[]) {
    this.fields = value;
  }
  @Input() selectedColumnNames: string[];
  @Input() options: any;
  @Input() groupByFields: GraphqlFilterSort[];

  @Output() sortChanged = new EventEmitter<Sort>();
  @Output() columnsChanged = new EventEmitter<CdkDragDrop<string>>();

  selectedFields: FieldDef[] = [];
  columns: string[] = [];
  fields: FieldDef[] = [];
  displayColumns = [] as string[];

  localeId = inject(LOCALE_ID);
  accountService = inject(AccountService);
  store = inject(Store);

  private _data: DataTableModel;

  get data(): DataTableModel {
    return this._data;
  }

  shouldReplaceZeros = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.ZERO_HANDLING)) === false;

  isGroup(index: number, item: any): boolean {
    return item.id;
  }

  getLabel(name: string & { id: number; label: string }): string {
    if (name?.label) return name?.label;
    return this.getFieldDef(name)?.label ?? name;
  }

  getFieldDef(name: string): FieldDef {
    return this.fields?.find((x) => x.name === name || x.name === `${name}(cf_name: "${name}")`);
  }

  private convertToField(name: string): FieldDef {
    return this.getFieldDef(name) ?? ({ name, label: name } as FieldDef);
  }

  isGroupRow(row: any): boolean {
    if (this.groupByFields?.length > 0) {
      return this.groupByFields.some((groupField) => !!row[groupField.field]);
    }

    return false;
  }

  isGroupLabel(column: string): boolean {
    return this.groupByFields?.map((groupField) => groupField.field).includes(column);
  }

  shouldStickColumn(column: any): boolean {
    if (this.groupByFields?.find((x) => x.field === column.name)) {
      return true;
    }

    return false;
  }

  canDrag(column: FieldDef): boolean {
    if (this.groupByFields?.find((x) => x.field === column.name)) {
      return false;
    }

    return true;
  }

  hideColumn(columnName: string): boolean {
    if (columnName === 'id') return true;
    if (columnName === 'pipeline_id') return !this.selectedColumnNames.includes('pipeline_id');
    return false;
  }

  getHeaderLabel(field: FieldDef): string {
    if (field.related_to_type === RelatedToType.Project && ['name', 'title'].includes(field.name)) {
      return 'Deals';
    }

    return field.label || field.name;
  }

  isSubTotalRow(row: any): boolean {
    return Object.keys(row).some((x) =>
      this.groupByFields.map((y) => y.field).includes(x) ? row?.[x] === 'Subtotal' : false,
    );
  }

  isTotalRow(row: any): boolean {
    const isNonGroupedTotal = row.Total === 'Total';
    const isGroupedTotal = this.groupByFields?.length > 0 && Object.keys(row).some((x) =>
      this.groupByFields.map((y) => y.field).includes(x) ? row?.[x] === 'Total' : false,
    );

    return isGroupedTotal || isNonGroupedTotal;
  }

  dropColumns(event: CdkDragDrop<string>): void {
    this.columnsChanged.emit(event);
  }

  getCellValue(column: any, row: any): string {
    let readableValue =
      new ReadableFieldPipe(this.localeId, this.accountService).transform(
        row[column.name],
        row,
        this.getFieldDef(column.related_to_type ? column.name : row.name),
        this.options,
      ) || this.getLabel(row[column.name]);
    readableValue = column.name !== 'time_series_field' ? readableValue : this.getLabel(row[column.name]);

    return readableValue !== null && readableValue !== undefined ? String(readableValue) : '';
  }
}
